import { ScopeButton, SelectButton } from '@monorepo/shared-lib';
import {
  DownOutlined,
  QuestionCircleOutlined,
  RightOutlined,
} from '@ant-design/icons';
import {
  Collapse,
  CollapseProps,
  List,
  Space,
  Spin,
  Tooltip,
  Typography,
} from 'antd';
import { AiScanningLogo } from 'components/Custom/AiScanningLogo';
import { ReactComponent as LogoExcel } from 'assets/images/icons/entityButtonExcel.svg';
import { ReactComponent as LogoQuickbooks } from 'assets/images/icons/entityButtonQuickbooks.svg';
import { ReactComponent as LogoXero } from 'assets/images/icons/entityButtonXero.svg';
import { ReactComponent as CheckIcon } from 'assets/images/icons/iconCheckFromWebsite.svg';
import { ReactComponent as CrossIcon } from 'assets/images/icons/iconCrossFromWebsite.svg';
import { ReactComponent as EnterpriseIcon } from 'assets/images/icons/iconEnterpriseColored.svg';
import { ReactComponent as LargeIcon } from 'assets/images/icons/iconLargeColored.svg';
import { ReactComponent as MediumIcon } from 'assets/images/icons/iconMediumColored.svg';
import { ReactComponent as SmallIcon } from 'assets/images/icons/iconSmallColored.svg';
import { ReactComponent as InfoMessageIcon } from 'assets/images/icons/infoMessageIcon.svg';
import UpgradeImage from 'assets/images/upgrade-pic.svg';
import ZohoSmallLogo from 'assets/images/zoho_small.png';
import { StyledCard } from 'components/Custom/card';
import { CustomTable } from 'components/Custom/custom-table';
import {
  BlackSpan,
  CardDescBoldSpan,
  CardDescGreenSpan,
  CardDescSpan,
  GreenLabel,
  TableItemSpan,
  TableTitleSpan,
} from 'components/Custom/spans';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Currency, SubscriptionPlan } from 'store/api/enums';
import { LicenseType } from 'store/app/enums';
import { UserLicence } from 'store/auth/models';
import { Feature } from 'store/billing/models';
import Features from 'store/billing/models/features.json';
import { SubscriptionType } from 'store/billing/models/subscriptionType';
import { SelectedPlan } from 'store/billing/types';
import styled, { createGlobalStyle } from 'styled-components';
import { getPriceByCurrency } from 'utils/calcAmountCurrency';
import {
  formatBillingPricingFeatureValue,
  getBillingPricingFeatureHelp,
  getBillingPricingFeatureLabel,
} from 'utils/billingPricingFeatureLabel';
import { useNumberFormat } from 'utils/numberFormat';
import { findSubscriptionPlanType } from 'utils/subscriptionPlanType';

const PricingHelpLinkStyle = createGlobalStyle`
  .pricing-help-link {
    color: var(--color-primary, #41aaaf);
    font-size: inherit;
    font-family: Inter, sans-serif;
    font-weight: 700;
    padding: 0;
    cursor: pointer;
    text-decoration: none;
    transition: text-decoration 0.2s;
  }

  .pricing-help-link:hover,
  .pricing-help-link:focus {
    text-decoration: underline !important;
    color: var(--color-primary, #41aaaf) !important;
  }
`;

const INTEGRATION_ICONS: Record<
  number,
  React.FC<React.SVGProps<SVGSVGElement>> | string | 'ai-scanning'
> = {
  21: LogoXero,
  22: LogoQuickbooks,
  24: ZohoSmallLogo,
  23: LogoExcel,
  142: 'ai-scanning',
};

type PricingFeatureRow = Feature & {
  rowKey: string;
  categoryKey?: string;
  isEmptySpacer?: boolean;
  isCollapsibleCategory?: boolean;
};

const buildPricingRows = (features: Feature[]): PricingFeatureRow[] => {
  let currentCategoryKey: string | undefined;
  const rows: PricingFeatureRow[] = [];

  features.forEach((feature, index) => {
    if (!feature.feature) {
      rows.push({
        ...(feature as Feature),
        rowKey: `spacer-${index}`,
        isEmptySpacer: true,
        categoryKey: currentCategoryKey,
        feature: '',
        helpText: ' ',
        small: ' ',
        medium: ' ',
        large: ' ',
        enterprise: ' ',
      });
      return;
    }

    const isCollapsibleCategory =
      Boolean(feature.isCategoryFeature) && !feature.hasPrice;
    if (isCollapsibleCategory) {
      currentCategoryKey = `category-${feature.featureId}`;
      rows.push({
        ...feature,
        rowKey: currentCategoryKey,
        categoryKey: currentCategoryKey,
        isCollapsibleCategory: true,
      });
      return;
    }

    rows.push({
      ...feature,
      rowKey: `feature-${feature.featureId}-${index}`,
      categoryKey: currentCategoryKey,
    });
  });

  return rows;
};

const filterVisiblePricingRows = (
  rows: PricingFeatureRow[],
  expandedCategories: Set<string>
): PricingFeatureRow[] =>
  rows.filter((row) => {
    if (row.isEmptySpacer) {
      return row.categoryKey ? expandedCategories.has(row.categoryKey) : true;
    }
    if (row.isCollapsibleCategory) {
      return true;
    }
    if (!row.categoryKey) {
      return true;
    }
    return expandedCategories.has(row.categoryKey);
  });

const getDefaultExpandedCategories = (): Set<string> =>
  new Set(
    buildPricingRows(Features as Feature[])
      .filter((row) => row.isCollapsibleCategory && row.categoryKey)
      .map((row) => row.categoryKey as string)
  );

const QuestionCollapseWrapper = styled.div`
  .ant-collapse {
    background: var(--color-surface-base, #ffffff) !important;
    color: var(--color-text-base, #2e2e2e);
  }

  .ant-collapse-borderless > .ant-collapse-item {
    border-bottom: 1px solid var(--color-border, #dbeae3);
  }

  .ant-collapse-header {
    padding: 16px 0px 16px 0px !important;
    color: var(--color-text-base, #2e2e2e) !important;
  }

  .ant-collapse-header-text,
  .ant-collapse-expand-icon,
  .ant-collapse-expand-icon .anticon {
    color: var(--color-text-base, #2e2e2e) !important;
  }

  .ant-collapse-content,
  .ant-collapse-content-box {
    color: var(--color-text-base, #2e2e2e) !important;
    background: transparent !important;
  }

  .ant-collapse-content-box {
    padding: 0px 0px 35px 0px !important;
  }

  .ant-collapse-item:hover > .ant-collapse-header {
    color: var(--color-text-base, #2e2e2e) !important;
  }
`;

/** Plan column tagline — Inter italic; raw <i> falls back to serif when italic face is missing. */
const PricingPlanTagline = styled.span`
  font-family: Inter, sans-serif;
  font-size: 12px;
  font-weight: 400;
  font-style: italic;
  line-height: 18px;
  color: var(--color-text-muted, #5a6a6a);
`;

interface PricingComponentProps {
  selectedPlan: SelectedPlan;
  activePlane: (
    plan: SubscriptionPlan,
    selectedCurrency: Currency,
    price: string,
    licenseId: string
  ) => void;
  subscriptionPlanTypes: SubscriptionType[];
  isLoading: boolean;
  renderContactUsWindow: (isEnterprise: boolean) => void;
  downgradePlan: (plan: string) => void;
  userLicence?: UserLicence;
}

const getFaqItems = (t: (key: string) => string): CollapseProps['items'] =>
  Array.from({ length: 16 }, (_, index) => {
    const key = String(index + 1);
    const baseKey = `billing.faq.${index + 1}`;
    return {
      key,
      label: (
        <CardDescBoldSpan
          fontSize="16px"
          $lineHeight="28px"
          style={{ color: 'var(--color-text-base, #2e2e2e)' }}
        >
          {t(`${baseKey}.q`)}
        </CardDescBoldSpan>
      ),
      children: (
        <CardDescSpan style={{ color: 'var(--color-text-base, #2e2e2e)' }}>
          {t(`${baseKey}.a`)}
        </CardDescSpan>
      ),
    };
  });

export const PricingComponent: React.FC<PricingComponentProps> = ({
  selectedPlan,
  activePlane,
  subscriptionPlanTypes,
  isLoading,
  renderContactUsWindow,
  downgradePlan,
  userLicence,
}) => {
  const { t } = useTranslation();
  const items = React.useMemo(() => getFaqItems(t), [t]);
  const { formatNumber, locale } = useNumberFormat();
  const [selectedCurrency, setSelectedCurrency] = React.useState<Currency>(
    selectedPlan.selectedCurrency
  );
  const [subscriptionTypes, setSubscriptionTypes] = React.useState<
    SubscriptionType[]
  >([]);
  const pricingRows = React.useMemo(
    () => buildPricingRows(Features as Feature[]),
    []
  );
  const [expandedCategories, setExpandedCategories] = React.useState<
    Set<string>
  >(() => getDefaultExpandedCategories());
  const isSubscribed = userLicence?.licenseType === LicenseType.PAID;

  const toggleCategory = React.useCallback((categoryKey: string) => {
    setExpandedCategories((prev) => {
      const next = new Set(prev);
      if (next.has(categoryKey)) {
        next.delete(categoryKey);
      } else {
        next.add(categoryKey);
      }
      return next;
    });
  }, []);

  const dataSource = React.useMemo(
    () => filterVisiblePricingRows(pricingRows, expandedCategories),
    [pricingRows, expandedCategories]
  );
  React.useEffect(() => {
    if (subscriptionPlanTypes) {
      //subscriptionPlanTypes.sort((a, b) => a?.description.replace(/\D/g, '100') - b?.description.replace(/\D/g, ''))
      //subscriptionPlanTypes.reverse();
      setSubscriptionTypes(subscriptionPlanTypes);
    }
  }, [subscriptionPlanTypes]);

  const formatPrice = (price: string) => {
    if (price === 'n/a') {
      return price;
    }

    const priceParts = price.toString().split(' ');
    const numericValue = parseFloat(priceParts[1]);

    if (!isNaN(numericValue)) {
      const formattedNumber =
        numericValue % 1 === 0
          ? formatNumber(numericValue, {
              maximumFractionDigits: 0,
              minimumFractionDigits: 0,
            })
          : formatNumber(numericValue);
      return `${priceParts[0]} ${formattedNumber}`;
    }
    return price;
  };

  const renderFeature = (content, record: PricingFeatureRow) => {
    const label = getBillingPricingFeatureLabel(record.featureId, content, t);
    const helpText = getBillingPricingFeatureHelp(
      record.featureId,
      record.helpText,
      t
    );

    if (content) {
      if (record.isCollapsibleCategory) {
        const isExpanded = expandedCategories.has(record.categoryKey ?? '');
        return (
          <TableItemSpan
            fontWeight="600"
            $lineHeight="35px"
            fontSize="14px"
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              cursor: 'pointer',
              userSelect: 'none',
            }}
            onClick={() => toggleCategory(record.categoryKey ?? '')}
          >
            {isExpanded ? (
              <DownOutlined
                style={{ fontSize: 12, color: 'var(--color-primary, #41aaaf)' }}
              />
            ) : (
              <RightOutlined
                style={{ fontSize: 12, color: 'var(--color-primary, #41aaaf)' }}
              />
            )}
            {label}
          </TableItemSpan>
        );
      }
      if (record.isCategoryFeature && record.hasPrice) {
        return (
          <TableItemSpan fontWeight="600" fontSize="18px" $lineHeight="87px">
            {label}{' '}
          </TableItemSpan>
        );
      } else {
        const integrationIcon = INTEGRATION_ICONS[record.featureId];
        const iconElement =
          integrationIcon === 'ai-scanning' ? (
            <AiScanningLogo size={20} />
          ) : typeof integrationIcon === 'string' ? (
            <img
              src={integrationIcon}
              alt=""
              width={20}
              height={20}
              style={{ flexShrink: 0 }}
            />
          ) : integrationIcon ? (
            React.createElement(integrationIcon, {
              width: 20,
              height: 20,
              style: { flexShrink: 0 },
            })
          ) : null;
        return (
          <TableItemSpan
            fontSize="14px"
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
            }}
          >
            {iconElement}
            <span>
              {label}{' '}
              {helpText && helpText !== ' ' && (
                <Tooltip title={helpText}>
                  <QuestionCircleOutlined
                    className="pricing-feature-help-icon"
                    style={{
                      fontSize: 15,
                      color: 'var(--color-text-muted, #a9b8b9)',
                      cursor: 'pointer',
                      marginBottom: -3,
                    }}
                  />
                </Tooltip>
              )}
            </span>
          </TableItemSpan>
        );
      }
    }
  };

  const getPlanFeature = (record: Feature, planType: SubscriptionPlan) => {
    if (!record.hasPrice) {
      switch (planType) {
        case SubscriptionPlan.SMALL_PLAN:
          return record.small;
          break;
        case SubscriptionPlan.MEDIUM_PLAN:
          return record.medium;
          break;
        case SubscriptionPlan.LARGE_PLAN:
          return record.large;
          break;
        case SubscriptionPlan.ENTERPRISE_PLAN:
          return record.enterprise;
          break;
        default:
          break;
      }
    }
  };

  /* const renderUpgradeButton = (
    plan: Plan,
    currentPlanIndex: number,
    index: number
  ) => {
    if (
      plan.planName?.toLowerCase() ==
      SubscriptionPlan.ENTERPRISE_PLAN.toLowerCase()
    ) {
      return (
        <ScopeButton //@ts-ignore
          type="primary"
          height="32px"
          width="123px"
          fontSize="13px"
          lineheight="22px"
          onClick={() => renderContactUsWindow(true)}
        >
          {t('billing.contact-us')}
        </ScopeButton>
      );
    } else {
      if (currentPlanIndex > index) {
        return (
          <ScopeButton //@ts-ignore
            type="primary"
            height="32px"
            width="130px"
            fontSize="13px"
            lineheight="22px"
            onClick={() => {
              const currentPlan = subscriptionPlanTypes.find(
                (type) => type.name.toLowerCase() == plan.planName.toLowerCase()
              );
              downgradePlan(currentPlan?.uuid);
            }}
          >
            {t('billing.downgrade-plan')}
          </ScopeButton>
        );
      } else if (currentPlanIndex < index) {
        return (
          <ScopeButton //@ts-ignore
            type="primary"
            height="32px"
            width="123px"
            fontSize="13px"
            lineheight="22px"
            onClick={() => upgradePlan(plan.planName)}
          >
            {t('billing.upgrade-plan')}
          </ScopeButton>
        );
      }
    }
  }; */

  const renderFeatureValue = (
    content,
    record: Feature,
    planType: SubscriptionPlan
  ) => {
    const currentSubscriptionType = findSubscriptionPlanType(
      subscriptionTypes,
      planType
    );

    const currentPlanTypeIndex = Object.values(SubscriptionPlan).findIndex(
      (plan) => plan.toLowerCase() == planType.toLowerCase()
    );
    const currentUserPlan = subscriptionTypes.find(
      (plan) => plan.uuid == userLicence?.licenceTypeId
    );

    const currentUserPlanIndex = Object.values(SubscriptionPlan).findIndex(
      (plan) => plan.toLowerCase() == currentUserPlan?.name.toLowerCase()
    );
    if (record.hasPrice && !record.isCategoryFeature) {
      const featurePrice = getPriceByCurrency(
        selectedCurrency,
        record.featureId,
        planType.replace(' Plan', '').toLowerCase(),
        true,
        locale
      );
      return (
        <TableItemSpan fontWeight="600" fontSize="14px">
          {featurePrice && formatPrice(featurePrice)}
          {record.hasPerEntity && featurePrice != 'n/a'
            ? ` / ${t('billing.per-entity')}`
            : ''}
        </TableItemSpan>
      );
    } else if (typeof content == 'boolean') {
      if (content) {
        return <CheckIcon width={16} height={16} />;
      } else {
        return <CrossIcon width={16} height={16} />;
      }
    } else if (
      typeof content === 'string' &&
      content.toLowerCase() === 'partial'
    ) {
      const tooltipText =
        record.featureId === 140
          ? t('billing.partial-view-attachments-tooltip')
          : getBillingPricingFeatureHelp(
              record.featureId,
              record.helpText,
              t
            ) || t('billing.partial-limited-functionality');
      return (
        <Tooltip title={tooltipText}>
          <span style={{ display: 'inline-block', cursor: 'help' }}>
            <InfoMessageIcon
              style={{ width: '16px', height: '16px', cursor: 'pointer' }}
            />
          </span>
        </Tooltip>
      );
    } else if (record.isCategoryFeature && record.hasPrice) {
      if (
        planType.toLowerCase() == SubscriptionPlan.ENTERPRISE_PLAN.toLowerCase()
      ) {
        return (
          <Space direction="vertical">
            <TableItemSpan fontWeight="600" fontSize="34px">
              {t('billing.por')}
            </TableItemSpan>
            <ScopeButton
              disabled={
                currentSubscriptionType?.uuid == userLicence?.licenceTypeId &&
                isSubscribed
              }
              type={
                currentSubscriptionType?.uuid == userLicence?.licenceTypeId &&
                isSubscribed
                  ? 'default'
                  : 'primary'
              }
              width={'110px'}
              height="35px"
              lineheight="12px"
              fontSize="13px"
              onClick={() => {
                renderContactUsWindow(true);
              }}
            >
              {currentSubscriptionType?.uuid == userLicence?.licenceTypeId &&
              isSubscribed
                ? t('billing.subscribed')
                : t('billing.contact-us')}
            </ScopeButton>
          </Space>
        );
      } else {
        return (
          <Space direction="vertical">
            <TableItemSpan fontWeight="600">
              <span style={{ fontSize: '34px' }}>
                {formatPrice(
                  getPriceByCurrency(
                    selectedCurrency,
                    record.featureId,
                    planType.replace(' Plan', '').toLowerCase(),
                    true,
                    locale
                  ) || ''
                )}
                <span style={{ fontSize: '16px' }}>
                  {' '}
                  / {t('billing.per-month-short')}
                </span>
              </span>
            </TableItemSpan>

            <ScopeButton
              disabled={
                currentSubscriptionType?.uuid == userLicence?.licenceTypeId &&
                isSubscribed
              }
              type="primary"
              width={'140px'}
              height="35px"
              lineheight="12px"
              fontSize="13px"
              onClick={() => {
                const plan = planType.split(' ')[0]?.toLowerCase();
                const priceFeature = Features.find(
                  (item) => item.featureId === 27
                );
                const matchedPlan = findSubscriptionPlanType(
                  subscriptionTypes,
                  planType
                );
                const licenseId = matchedPlan?.uuid || planType;
                if (priceFeature) {
                  if (
                    currentUserPlanIndex < currentPlanTypeIndex ||
                    !isSubscribed
                  ) {
                    activePlane(
                      planType,
                      selectedCurrency,
                      priceFeature[plan],
                      licenseId
                    );
                  } else if (matchedPlan?.uuid) {
                    downgradePlan(matchedPlan.uuid);
                  }
                }
              }}
            >
              {
                currentSubscriptionType?.uuid == userLicence?.licenceTypeId &&
                isSubscribed
                  ? t('billing.subscribed')
                  : isSubscribed
                  ? currentUserPlanIndex < currentPlanTypeIndex
                    ? t('billing.upgrade-plan')
                    : t('billing.downgrade-plan')
                  : t('billing.activate') /*t('billing.activate')*/
              }
            </ScopeButton>
          </Space>
        );
      }
    } else if (record.hasPerEntity && record.hasPrice) {
    } else {
      const planValue = getPlanFeature(record, planType);
      return (
        <TableItemSpan fontWeight="600">
          {typeof planValue === 'string'
            ? formatBillingPricingFeatureValue(planValue, t)
            : planValue}
        </TableItemSpan>
      );
    }
  };

  const columns = [
    {
      title: (
        <CardDescBoldSpan fontSize="24px" fontWeight="400">
          {t('billing.our-pricing')}
        </CardDescBoldSpan>
      ),
      dataIndex: 'feature',
      //key: 'organisation',
      width: '200px',
      render: renderFeature,
    },
    {
      title: (
        <>
          <Space
            direction="vertical"
            size={'small'}
            style={{ padding: '0px 10px' }}
          >
            <SmallIcon width={55} height={55} />
            <TableTitleSpan>{t('billing.plan-small-label')}</TableTitleSpan>
            <PricingPlanTagline>
              {t('billing.plan-small-tagline')}
            </PricingPlanTagline>
          </Space>
        </>
      ),
      dataIndex: 'small',
      //key: 'organisation',
      width: '170px',
      textWrap: 'word-break',
      align: 'center',
      //ellipsis: true,
      render: (content, record: Feature) =>
        renderFeatureValue(content, record, SubscriptionPlan.SMALL_PLAN),
    },
    {
      width: '10px',
    },
    {
      title: (
        <Space
          direction="vertical"
          size={'small'}
          style={{ padding: '0px 10px' }}
        >
          <MediumIcon width={55} height={55} />
          <TableTitleSpan>{t('billing.plan-medium-label')}</TableTitleSpan>
          <PricingPlanTagline>
            {t('billing.plan-medium-tagline')}
          </PricingPlanTagline>
        </Space>
      ),
      dataIndex: 'medium',
      //key: 'organisation',
      width: '170px',
      textWrap: 'word-break',
      align: 'center',
      render: (content, record: Feature) =>
        renderFeatureValue(content, record, SubscriptionPlan.MEDIUM_PLAN),
    },
    {
      width: '10px',
    },
    {
      title: (
        <>
          <GreenLabel>{t('billing.most-popular')}</GreenLabel>

          <Space
            direction="vertical"
            size={'small'}
            style={{ padding: '0px 10px', justifyContent: 'flex-start' }}
          >
            <LargeIcon width={55} height={55} />
            <TableTitleSpan>{t('billing.plan-large-label')}</TableTitleSpan>
            <PricingPlanTagline>
              {t('billing.plan-large-tagline')}
            </PricingPlanTagline>
          </Space>
        </>
      ),
      dataIndex: 'large',
      //key: 'organisation',
      width: '170px',
      textWrap: 'word-break',
      align: 'center',
      render: (content, record: Feature) =>
        renderFeatureValue(content, record, SubscriptionPlan.LARGE_PLAN),
    },
    {
      width: '10px',
    },
    {
      title: (
        <Space
          direction="vertical"
          size={'small'}
          style={{ padding: '0px 10px', justifyContent: 'flex-start' }}
        >
          <EnterpriseIcon width={55} height={55} />
          <TableTitleSpan>{t('billing.plan-enterprise-label')}</TableTitleSpan>
          <PricingPlanTagline>
            {t('billing.plan-enterprise-tagline')}
          </PricingPlanTagline>
        </Space>
      ),
      dataIndex: 'enterprise',
      //key: 'organisation',
      width: '170px',
      textWrap: 'word-break',
      align: 'center',
      render: (content, record: Feature) =>
        renderFeatureValue(content, record, SubscriptionPlan.ENTERPRISE_PLAN),
    },
    {
      width: '10px',
    },
  ];

  return (
    <Spin spinning={isLoading} size="large">
      <PricingHelpLinkStyle />
      <div
        className="pricing-page"
        style={{ padding: '20px 80px', minWidth: '1250px', maxWidth: '1750px' }}
      >
        <Space
          style={{
            columnGap: '82px',
            width: '100%',
            marginBottom: '60px',
          }}
        >
          <StyledCard
            height="1090px"
            width="851px"
            $borderless
            $stopshadowbox
            $nopadding
            $removeHeader
            bg={'linear-gradient(180deg, #EDFAF6 0%, #E1F4FE 100%)'}
          >
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                flexFlow: 'row',
                alignContent: 'normal',
              }}
            >
              <div
                style={{
                  padding: '20px 0px',
                  paddingTop: '48px',
                  paddingLeft: '95px',
                }}
              >
                <Typography
                  style={{
                    fontSize: '24px',
                    fontWeight: '600',
                    lineHeight: '26px',
                    textAlign: 'left',
                    color: 'var(--color-text-dark, #042a2a)',
                    marginBottom: '32px',
                  }}
                >
                  <CardDescBoldSpan fontWeight="700" fontSize="35px">
                    {t('billing.upgrade-your-plan')}
                  </CardDescBoldSpan>
                </Typography>
                <List
                  style={{ marginBottom: '15px' }}
                  dataSource={[
                    t('billing.more-vat-functionality'),
                    t('billing.connect-more-entities'),
                    t('billing.unlimited-users'),
                    t('billing.integrations-xero-excel-more'),
                  ]}
                  split={false}
                  size="small"
                  renderItem={(item) => (
                    <List.Item style={{ padding: '2px 3px' }}>
                      <CheckIcon
                        style={{
                          width: '15px',
                          height: '15px',
                          marginTop: '2px',
                          flexShrink: 0,
                        }}
                      />{' '}
                      <BlackSpan>{item}</BlackSpan>
                    </List.Item>
                  )}
                />
              </div>
              <img
                alt={t('billing.upgrade-image-alt')}
                style={{ width: '404px' }}
                src={UpgradeImage}
              />
            </div>
          </StyledCard>
          <Typography
            style={{
              fontSize: '32px',
              fontWeight: '600',
              lineHeight: '34px',
              textAlign: 'left',
              color: 'var(--color-text-dark, #042a2a)',
              marginBottom: '10px',
              minWidth: 200,
            }}
          >
            <CardDescGreenSpan fontSize="32px" fontWeight="700">
              {t('billing.unlock-all-features')}
            </CardDescGreenSpan>
            <br />
            {t('billing.enhance-capture')}
            <br />
            {t('billing.simplify-vat')}
            <br />
            {t('billing.amplify-efficiency')}
            <br />
          </Typography>
        </Space>

        <Space
          align="center"
          style={{
            width: '100%',
            justifyContent: 'start',
            marginBottom: '30px',
            columnGap: 280,
          }}
        >
          <span
            style={{
              fontFamily: 'Inter, sans-serif',
              fontSize: '14px',
              color: 'var(--color-text-dark, #042a2a)',
            }}
          >
            {t('billing.pricing-help-prefix')}{' '}
            <a
              href="#"
              className="pricing-help-link"
              onClick={(e) => {
                e.preventDefault();
                renderContactUsWindow(false);
              }}
            >
              {t('billing.chat-with-us')}
            </a>{' '}
            {t('billing.or')}{' '}
            <a href="#FAQs" className="pricing-help-link">
              {t('billing.browse-faqs')}
            </a>
          </span>
          <Space>
            {Object.keys(Currency).map((key) => {
              const isSelected = selectedCurrency == key;
              return (
                <SelectButton
                  key={key}
                  type="default"
                  width="55px"
                  height="30px"
                  lineheight="12px"
                  fontSize="12px"
                  selected={isSelected}
                  onClick={() => setSelectedCurrency(key as Currency)}
                  className={
                    isSelected
                      ? 'pricing-currency-button pricing-currency-button--selected'
                      : 'pricing-currency-button'
                  }
                >
                  {Currency[key]}
                </SelectButton>
              );
            })}
          </Space>
        </Space>
        <div style={{ marginBottom: '90px', marginTop: '30px' }}>
          <CustomTable //@ts-ignore
            columns={columns}
            dataSource={dataSource}
            rowKey="rowKey"
            pagination={false}
          />
        </div>
        <TableItemSpan fontSize="24px" fontWeight="400" $lineHeight="34px">
          {t('billing.faqs')}
        </TableItemSpan>
        <div
          style={{
            display: 'flex',
            flexFlow: 'row',
            columnGap: '120px',
            paddingTop: '30px',
          }}
        >
          <StyledCard
            height="315px"
            width="265px"
            $borderless
            $stopshadowbox
            $nopadding
            $removeHeader
            bg_color={'var(--color-layout-bg, #E9F6F7)'}
          >
            <div
              style={{
                display: 'flex',
                flexFlow: 'row',
                columnGap: '150px',
              }}
            >
              <div
                style={{
                  padding: '110px 48px 35px 44px',
                }}
              >
                <Space direction="vertical" style={{ rowGap: 20 }}>
                  <CardDescBoldSpan fontSize="24px" $lineHeight="28px">
                    {t('billing.have-a-question')}
                  </CardDescBoldSpan>

                  <a
                    href="https://generate.tax/contact/"
                    target="_blank"
                    rel="noreferrer"
                  >
                    <ScopeButton
                      type="primary"
                      width="111px"
                      height="30px"
                      fontWeight="700"
                      fontSize="13px"
                      lineheight="20px"
                    >
                      {t('billing.contact-us')}
                    </ScopeButton>
                  </a>
                </Space>
              </div>
            </div>
          </StyledCard>
          <QuestionCollapseWrapper id="FAQs">
            <Collapse
              style={{
                width: '100%',
                backgroundColor: 'var(--color-surface-base, #ffffff)',
              }}
              defaultActiveKey={['1']}
              bordered={false}
              //onChange={onChange}
              expandIconPosition={'end'}
              items={items}
            />
          </QuestionCollapseWrapper>
        </div>
      </div>
    </Spin>
  );
};
